export interface LineArena {
  id: string;
  name: string;
  kind: 'Test' | 'Official';
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  description: string;
  /** Path waypoints in meters (XZ plane), start → finish. Rendered as a Catmull-Rom spline. */
  waypoints: [number, number][];
  /** Overrides the rule pack's line width (cm) for this arena, if set. */
  lineWidthCm?: number;
}

/** Practice + official Line Follower tracks. Students pick one before simulating. */
export const lineArenas: LineArena[] = [
  {
    id: 'test1',
    name: 'Test Track 1',
    kind: 'Test',
    difficulty: 'Beginner',
    description: 'Wide sweeping curves and long straights — learn the controls with no sharp turns.',
    waypoints: [
      [-2.4, -1.2],
      [-1, -1.6],
      [0.5, -1.5],
      [1.8, -0.8],
      [2.3, 0.3],
      [1.7, 1.3],
      [0.3, 1.7],
      [-1.2, 1.5],
      [-2.2, 0.6],
    ],
  },
  {
    id: 'test2',
    name: 'Test Track 2',
    kind: 'Test',
    difficulty: 'Beginner',
    description: 'A couple of tighter corners mixed in — good next step once you can hold Test Track 1.',
    waypoints: [
      [-2, -1.5],
      [-0.5, -1.7],
      [1.2, -1.4],
      [2.1, -0.5],
      [1.6, 0.6],
      [0.4, 0.4],
      [-0.4, 1.1],
      [0.6, 1.8],
      [1.9, 1.6],
      [-1.2, 1.9],
      [-2.2, 1.0],
      [-1.9, -0.3],
    ],
  },
  {
    id: 'official1',
    name: 'Official Arena 1 — Day 1',
    kind: 'Official',
    difficulty: 'Advanced',
    description: 'The real Day 1 competition track — digitized from the official arena design. Dense S-curves, a chicane, and a long staircase section punish speed tuned too high.',
    waypoints: [
      [0.888, -0.776],
      [0.776, -0.776],
      [0.776, -0.898],
      [0.757, -0.941],
      [0.712, -0.975],
      [0.629, -0.97],
      [0.576, -0.901],
      [0.576, -0.448],
      [0.597, -0.408],
      [0.645, -0.386],
      [0.72, -0.386],
      [0.725, -0.472],
      [0.784, -0.474],
      [0.797, -0.536],
      [0.845, -0.584],
      [0.909, -0.586],
      [0.954, -0.554],
      [0.968, -0.522],
      [0.965, -0.381],
      [0.81, -0.261],
      [0.968, -0.128],
      [0.816, 0.0],
      [0.968, 0.149],
      [0.813, 0.296],
      [0.97, 0.44],
      [0.824, 0.568],
      [0.821, 0.635],
      [0.536, 0.819],
      [0.381, 0.821],
      [0.373, 0.973],
      [0.261, 0.885],
      [0.139, 0.965],
      [0.136, 0.627],
      [-0.045, 0.632],
      [-0.053, 0.968],
      [-0.251, 0.968],
      [-0.435, 0.819],
      [-0.621, 0.955],
      [-0.613, 0.453],
      [-0.315, 0.453],
      [-0.304, 0.283],
      [0.056, 0.283],
      [0.064, 0.365],
      [0.192, 0.365],
      [0.536, 0.277],
      [0.613, 0.094],
      [0.408, 0.094],
      [0.237, -0.061],
      [0.376, -0.2],
      [0.379, -0.221],
      [0.12, -0.221],
      [-0.077, -0.074],
      [-0.072, -0.053],
      [0.08, 0.064],
      [0.091, 0.094],
      [-0.235, 0.094],
      [-0.44, -0.064],
      [-0.256, -0.202],
      [-0.253, -0.362],
      [0.069, -0.365],
      [0.109, -0.386],
      [0.136, -0.434],
      [0.128, -0.496],
      [0.083, -0.536],
      [-0.307, -0.549],
      [-0.349, -0.592],
      [-0.341, -0.661],
      [-0.267, -0.701],
      [0.032, -0.701],
      [0.035, -0.856],
      [0.051, -0.858],
      [0.427, -0.618],
      [0.429, -0.965],
      [0.405, -0.986],
      [-0.581, -0.986],
      [-0.93, -0.656],
      [-0.928, -0.629],
      [-0.816, -0.522],
      [-0.818, 0.144],
      [-0.88, 0.2],
      [-0.829, 0.16],
      [-0.736, 0.269],
      [-0.837, 0.381],
      [-0.837, 0.547],
    ],
  },
  {
    id: 'official2',
    name: 'Official Arena 2',
    kind: 'Official',
    difficulty: 'Advanced',
    description: 'Tight hairpins and a short radius throughout — punishes speed that is tuned too high.',
    waypoints: [
      [-2.2, -1.7],
      [-1.0, -1.9],
      [-0.3, -1.0],
      [-1.2, -0.6],
      [-1.9, 0.2],
      [-1.0, 0.9],
      [0.0, 0.5],
      [0.0, -0.4],
      [1.0, -0.8],
      [2.0, -1.5],
      [2.3, -0.3],
      [1.4, 0.4],
      [1.9, 1.4],
      [0.8, 1.9],
      [-0.4, 1.5],
      [-1.5, 1.8],
    ],
  },
  {
    id: 'official3',
    name: 'Official Arena 3',
    kind: 'Official',
    difficulty: 'Advanced',
    description: 'The longest official circuit — long straights for top speed, then a technical inner loop.',
    waypoints: [
      [-2.4, -1.9],
      [0, -2.0],
      [2.3, -1.9],
      [2.4, -0.8],
      [1.2, -0.5],
      [1.3, 0.4],
      [2.4, 0.6],
      [2.3, 1.9],
      [0, 1.9],
      [-1.0, 1.6],
      [-1.2, 0.6],
      [-0.2, 0.3],
      [-0.3, -0.7],
      [-1.5, -0.9],
      [-2.4, -0.4],
    ],
  },
];

export function getLineArena(id: string | undefined): LineArena {
  return lineArenas.find((a) => a.id === id) ?? lineArenas[0];
}
